// src/components/staff/TodaysRoster.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useStudentContext } from '../../contexts/StudentContext';
import { isFormValid } from '../../utils/helpers';
import { exportToCSV } from '../../utils/helpers';
import { styles } from '../../styles/theme';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { parseMMDDYYYY } from '../../utils/dateUtils';
import { isBHStudent, isCampStudentOnDate, buildFirstAttendanceMap, isNewStudentOnDate } from '../../utils/studentScheduleUtils';
import { useLocation } from '../../contexts/LocationContext';

// Edit After School Student Modal Component
const EditAfterSchoolStudentModal = ({ student, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    pickupDays: (() => {
      if (Array.isArray(student.pickupDays)) {
        return student.pickupDays;
      }
      if (typeof student.pickupDays === 'string') {
        return student.pickupDays.split(/[,\n]+/).map(d => d.trim()).filter(d => d);
      }
      return [];
    })(),
    dismissalTimeNormal: student.dismissalTimeNormal || '15:30',
    dismissalTimeWednesday: student.dismissalTimeWednesday || '14:15',
    programDaysPerWeek: student.programDaysPerWeek || '4 days'
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      pickupDays: prev.pickupDays.includes(day)
        ? prev.pickupDays.filter(d => d !== day)
        : [...prev.pickupDays, day]
    }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        ...formData,
        pickupDays: formData.pickupDays.join(', '),
        programDaysPerWeek: `${formData.pickupDays.length} days`
      };
      await onSave(student.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save changes');
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '8px', padding: '2rem',
        maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto'
      }}>
        <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>Edit After School Schedule</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            Student: {student.firstName} {student.lastName}
          </h3>
          <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Program: {student.programSelection}</p>
        </div>

        {/* Pickup Days Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
            Pickup Days *
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {daysOfWeek.map(day => (
              <label key={day} style={{
                display: 'flex', alignItems: 'center', padding: '0.75rem',
                border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer',
                backgroundColor: formData.pickupDays.includes(day) ? '#e0f2fe' : 'white'
              }}>
                <input type="checkbox" checked={formData.pickupDays.includes(day)}
                  onChange={() => toggleDay(day)} style={{ marginRight: '0.75rem' }} />
                <span style={{
                  color: formData.pickupDays.includes(day) ? '#0c4a6e' : '#374151',
                  fontWeight: formData.pickupDays.includes(day) ? '600' : '400'
                }}>
                  {day}
                </span>
              </label>
            ))}
          </div>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Selected: {formData.pickupDays.length} day(s) per week
          </p>
        </div>

        {/* Dismissal Times */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
            Dismissal Time (Mon/Tue/Thu/Fri)
          </label>
          <input type="time" value={formData.dismissalTimeNormal}
            onChange={(e) => setFormData(prev => ({ ...prev, dismissalTimeNormal: e.target.value }))}
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }} />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
            Dismissal Time (Wednesday)
          </label>
          <input type="time" value={formData.dismissalTimeWednesday}
            onChange={(e) => setFormData(prev => ({ ...prev, dismissalTimeWednesday: e.target.value }))}
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }} />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button onClick={handleSave} disabled={formData.pickupDays.length === 0}
            style={{
              flex: 1, padding: '0.75rem',
              backgroundColor: formData.pickupDays.length === 0 ? '#d1d5db' : '#00A3E0',
              color: 'white', border: 'none', borderRadius: '4px', fontWeight: '600',
              cursor: formData.pickupDays.length === 0 ? 'not-allowed' : 'pointer'
            }}>
            Save Changes
          </button>
          <button onClick={onClose} style={{
            flex: 1, padding: '0.75rem', backgroundColor: 'white', color: '#374151',
            border: '1px solid #d1d5db', borderRadius: '4px', fontWeight: '600', cursor: 'pointer'
          }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const TodaysRoster = ({ onBack, onNavigate }) => {
  const { allStudents, attendanceRecords } = useStudentContext();
  const firstAttendanceMap = buildFirstAttendanceMap(attendanceRecords);
  const { currentLocation } = useLocation();
  const [todayFilter, setTodayFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [displayDate, setDisplayDate] = useState(new Date());
  const [displayDay, setDisplayDay] = useState('');
  const [editingStudent, setEditingStudent] = useState(null);
  const [todaySchedules, setTodaySchedules] = useState([]);
  const [schedulePublished, setSchedulePublished] = useState(false);

  // Helper to format date as MM-DD-YY
  const formatDateMMDDYY = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}-${day}-${year}`;
  };

  const isScheduledForDate = (student, date) => {
    if (student.archived) return false;

    // Never show students on weekends — no programs run Sat or Sun
    const dow = date.getDay();
    if (dow === 0 || dow === 6) return false;

    // Inactive — but respect future inactiveDate
    if (student.programStatus === 'inactive') {
      if (student.inactiveDate) {
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        const inactiveFrom = new Date(student.inactiveDate + 'T00:00:00');
        inactiveFrom.setHours(0, 0, 0, 0);
        if (checkDate >= inactiveFrom) return false;
      } else {
        return false;
      }
    }

    if (student.startDate) {
      const studentStart = parseMMDDYYYY(student.startDate);
      if (studentStart) {
        const checkDate = new Date(date);
        studentStart.setHours(0, 0, 0, 0);
        checkDate.setHours(0, 0, 0, 0);
        if (studentStart > checkDate) return false;
      }
    }

    const dateString = formatDateMMDDYY(date);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });

   if (student.careDates && Array.isArray(student.careDates)) {
      const hasDate = student.careDates.some(care => care.date === dateString);
      if (hasDate) return true;
    }

    // Camp students with start/end date range but no careDates array
  // Camp students with start/end date range but no careDates array
    // NOTE: BH students should ONLY show via careDates, never via date range
    if (student.campStartDate && student.campEndDate) {
      const isBH = student.billingType === 'bright-horizons' || 
                   student.billingType === 'third-party' ||
                   (student.programSelection || '').toLowerCase().includes('bright horizons');
      if (!isBH) {
        const parseDate = (str) => {
          if (!str) return null;
          const parts = str.split('-');
          if (parts[0].length === 4) return new Date(str + 'T00:00:00');
          return new Date(`${parts[2]}-${parts[0]}-${parts[1]}T00:00:00`);
        };
        const campStart = parseDate(student.campStartDate);
        const campEnd = parseDate(student.campEndDate);
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        if (campStart && campEnd && checkDate >= campStart && checkDate <= campEnd) {
          const dow = checkDate.getDay();
          if (dow !== 0 && dow !== 6) return true;
        }
      }
    }

    if (student.programDaysPerWeek === '5 days' || student.programDaysPerWeek === '5') {
      if (weekday !== 'Saturday' && weekday !== 'Sunday') return true;
    }

    if (student.pickupDays) {
      const days = Array.isArray(student.pickupDays)
        ? student.pickupDays
        : student.pickupDays.split(/[,\n]+/).map(d => d.trim()).filter(d => d);
      return days.includes(weekday);
    }

    return false;
  };

  // Find next available WEEKDAY with students
  // Rules: Sat → next Mon, Sun → next Mon, Mon-Fri → today or next weekday
  useEffect(() => {
    const findNextAvailableDay = () => {
      const today = new Date();
      const dow = today.getDay(); // 0=Sun, 6=Sat

      // If today is Sat or Sun, jump straight to next Monday
      let startDate = new Date(today);
      if (dow === 0) {
        // Sunday → next Monday
        startDate.setDate(today.getDate() + 1);
      } else if (dow === 6) {
        // Saturday → next Monday
        startDate.setDate(today.getDate() + 2);
      }

      // From startDate, find the next weekday (Mon-Fri) that has students
      for (let i = 0; i <= 14; i++) {
        const loopDate = new Date(startDate);
        loopDate.setDate(startDate.getDate() + i);
        const loopDow = loopDate.getDay();
        // Skip weekends
        if (loopDow === 0 || loopDow === 6) continue;
        const hasStudents = allStudents.some(student => isScheduledForDate(student, loopDate));
        if (hasStudents) {
          setDisplayDate(loopDate);
          setDisplayDay(loopDate.toLocaleDateString('en-US', { weekday: 'long' }));
          return;
        }
      }

      // Fallback — show startDate even if no students
      setDisplayDate(startDate);
      setDisplayDay(startDate.toLocaleDateString('en-US', { weekday: 'long' }));
    };

    findNextAvailableDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStudents]);

  let todaysStudents = allStudents.filter(student => {
    if (!isScheduledForDate(student, displayDate)) return false;

    if (student.onBreak === true) {
      const td = new Date(displayDate);
      const displayISO = `${td.getFullYear()}-${String(td.getMonth()+1).padStart(2,'0')}-${String(td.getDate()).padStart(2,'0')}`;
      const breakStarted = !student.breakStartDate || displayISO >= student.breakStartDate;
      const breakNotEnded = !student.breakEndDate || displayISO <= student.breakEndDate;
      if (breakStarted && breakNotEnded) return false;
      if (!student.breakEndDate) return false;
    }

    const formatDate = (date) => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    };

    const todayStr = formatDate(new Date());
    const displayStr = formatDate(displayDate);
    if (todayStr === displayStr && student.attendanceStatus === 'absent') return false;

    return true;
  });

  // Apply program filter
  if (todayFilter !== 'all') {
    todaysStudents = todaysStudents.filter(student => {
      if (todayFilter === 'pickup') return student.needsPickup;
      if (todayFilter === 'dropoff') return !student.needsPickup && student.programSelection?.includes('Drop');
      if (todayFilter === 'camp') return isCampStudentOnDate(student, displayDate);
      if (todayFilter === 'saturday') return student.programSelection?.includes('Saturday');
      return true;
    });
  }

  // Apply search filter
  if (searchTerm) {
    const search = searchTerm.toLowerCase();
    todaysStudents = todaysStudents.filter(student =>
      student.firstName?.toLowerCase().includes(search) ||
      student.lastName?.toLowerCase().includes(search) ||
      student.parentName?.toLowerCase().includes(search)
    );
  }

  const handleExport = () => {
    const filename = `students-${formatDateMMDDYY(displayDate)}.csv`;
    exportToCSV(todaysStudents, filename);
  };

  const handleSaveAfterSchoolStudent = async (studentId, updateData) => {
    try {
      const studentRef = doc(db, 'students', studentId);
      await updateDoc(studentRef, { ...updateData, lastUpdated: serverTimestamp() });
      alert('Schedule updated successfully!');
    } catch (error) {
      console.error('❌ Error updating student:', error);
      alert(`Failed to update: ${error.message}`);
      throw error;
    }
  };

  const isToday = displayDate.toDateString() === new Date().toDateString();

  // Load staff schedules for the displayed date — filtered by currentLocation
  useEffect(() => {
    const loadSchedules = async () => {
      try {
        const dateStr = (() => {
          const m = String(displayDate.getMonth() + 1).padStart(2, '0');
          const d = String(displayDate.getDate()).padStart(2, '0');
          const y = displayDate.getFullYear();
          return `${m}-${d}-${y}`;
        })();

        // Check if this week's schedule is published
        const dayOfWeek = displayDate.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(displayDate);
        monday.setDate(displayDate.getDate() + diff);
        const wm = String(monday.getMonth() + 1).padStart(2, '0');
        const wd = String(monday.getDate()).padStart(2, '0');
        const wy = monday.getFullYear();
        const weekKey = `${wm}-${wd}-${wy}`;
        const weekMetaSnap = await getDoc(doc(db, 'schedule_weeks', weekKey));
        const published = weekMetaSnap.exists() ? weekMetaSnap.data().published === true : false;
        setSchedulePublished(published);
        if (!published) {
          setTodaySchedules([]);
          return;
        }

        const [schedSnap, progSnap] = await Promise.all([
          getDocs(query(collection(db, 'schedules'), where('date', '==', dateStr))),
          getDocs(collection(db, 'programs')),
        ]);

        const activePrograms = progSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.active !== false);

        const normalize = (s) => (s || '').toLowerCase().replace(/[-\s]+/g, '');
        const activeProgramIds = new Set(activePrograms.map(p => p.id));

        const isValid = (schedule) => {
          // Location filter — same rule as everywhere else
          if (currentLocation !== 'all') {
            const schedLocation = schedule.locationId === 'kirkland' ? 'kirkland' : 'redmond';
            if (schedLocation !== currentLocation) return false;
          }
          // Must belong to an active program
          if (schedule.programId) return activeProgramIds.has(schedule.programId);
          return activePrograms.some(p =>
            normalize(p.name) === normalize(schedule.programType) ||
            (p.legacyProgramTypes || []).some(t => normalize(t) === normalize(schedule.programType))
          );
        };

        const allSchedules = schedSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setTodaySchedules(allSchedules.filter(isValid));
      } catch (err) {
        setTodaySchedules([]);
      }
    };
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayDate, currentLocation]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <div style={styles.header}>
        <div style={styles.centerContainer}>
          <button style={styles.backButton} onClick={onBack}>
            ← Back to Staff Dashboard
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 style={styles.headerTitle}>Student Roster — {displayDay}</h1>
              <p style={styles.headerSubtitle}>
                {displayDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                {' · '}
                <span style={{ fontWeight: '700', opacity: 0.9 }}>
                  Stemtree {currentLocation === 'kirkland' ? 'Kirkland' : currentLocation === 'all' ? 'All Locations' : 'Redmond'}
                </span>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={() => { const d = new Date(displayDate); d.setDate(d.getDate() - 1); if (d.getDay() === 0) d.setDate(d.getDate() - 2); if (d.getDay() === 6) d.setDate(d.getDate() - 1); setDisplayDate(d); setDisplayDay(d.toLocaleDateString('en-US', { weekday: 'long' })); }}
                style={{ padding: '0.4rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1.5px solid white', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <ChevronLeft style={{ width: '16px', height: '16px' }} />
              </button>
              <button onClick={() => { const t = new Date(); setDisplayDate(t); setDisplayDay(t.toLocaleDateString('en-US', { weekday: 'long' })); }}
                style={{ padding: '0.4rem 0.85rem', backgroundColor: isToday ? 'white' : 'rgba(255,255,255,0.2)', color: isToday ? '#3b82f6' : 'white', border: '1.5px solid white', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700' }}>
                Today
              </button>
              <button onClick={() => { const d = new Date(displayDate); d.setDate(d.getDate() + 1); if (d.getDay() === 6) d.setDate(d.getDate() + 2); if (d.getDay() === 0) d.setDate(d.getDate() + 1); setDisplayDate(d); setDisplayDay(d.toLocaleDateString('en-US', { weekday: 'long' })); }}
                style={{ padding: '0.4rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1.5px solid white', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <ChevronRight style={{ width: '16px', height: '16px' }} />
              </button>
              <input type="date"
                value={`${displayDate.getFullYear()}-${String(displayDate.getMonth()+1).padStart(2,"0")}-${String(displayDate.getDate()).padStart(2,"0")}`}
                onChange={e => { const d = new Date(e.target.value + 'T00:00:00'); setDisplayDate(d); setDisplayDay(d.toLocaleDateString('en-US', { weekday: 'long' })); }}
                style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1.5px solid white', fontSize: '0.8rem', fontWeight: '600', backgroundColor: 'white', color: '#374151', cursor: 'pointer' }} />
            </div>
          </div>
        </div>
      </div>

      <div style={styles.centerContainer}>
        {/* Weekly Day Grid */}
        {(() => {
          const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const d = new Date(displayDate);
          const dow = d.getDay();
          const monday = new Date(d);
          monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));

          return (
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', backgroundColor: 'white', borderRadius: '12px', padding: '0.85rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', alignItems: 'stretch' }}>
              {weekDays.map((dayName, i) => {
                const dayDate = new Date(monday);
                dayDate.setDate(monday.getDate() + i);
                const localISO = (() => { const t = new Date(dayDate); return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`; })();
                const count = allStudents.filter(s => {
                  if (!isScheduledForDate(s, dayDate)) return false;
                  if (s.onBreak === true) {
                    const breakStarted = !s.breakStartDate || localISO >= s.breakStartDate;
                    const breakNotEnded = !s.breakEndDate || localISO <= s.breakEndDate;
                    if (breakStarted && breakNotEnded) return false;
                    if (!s.breakEndDate) return false;
                  }
                  return true;
                }).length;
                const isSelected = dayDate.toDateString() === displayDate.toDateString();
                const isTodayDay = dayDate.toDateString() === new Date().toDateString();
                return (
                  <div key={dayName} onClick={() => { setDisplayDate(dayDate); setDisplayDay(dayName); }}
                    style={{
                      flex: 1, textAlign: 'center', padding: '0.6rem 0.25rem',
                      borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s',
                      backgroundColor: isSelected ? '#3b82f6' : isTodayDay ? '#eff6ff' : '#f8fafc',
                      border: isSelected ? '2px solid #3b82f6' : isTodayDay ? '2px solid #bfdbfe' : '2px solid transparent',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = isTodayDay ? '#eff6ff' : '#f8fafc'; }}
                  >
                    <div style={{ fontSize: '0.65rem', fontWeight: '700', color: isSelected ? 'white' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {dayName.slice(0, 3)}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: isSelected ? 'rgba(255,255,255,0.8)' : '#9ca3af', marginBottom: '0.3rem' }}>
                      {dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '800', color: isSelected ? 'white' : count > 0 ? '#1e293b' : '#d1d5db', lineHeight: 1 }}>
                      {count}
                    </div>
                    <div style={{ fontSize: '0.6rem', color: isSelected ? 'rgba(255,255,255,0.7)' : '#9ca3af', marginTop: '0.15rem' }}>
                      {count === 1 ? 'student' : 'students'}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Summary Stats */}
        {(() => {
          const isBH = isBHStudent;
          const withPickup  = todaysStudents.filter(s => s.needsPickup);
          const dropOff     = todaysStudents.filter(s => !s.needsPickup && s.programSelection?.toLowerCase().includes('after school'));
          const camp        = todaysStudents.filter(s => (s.programSelection?.toLowerCase().includes('camp') || s.programSelection?.toLowerCase().includes('full-day')) && !isBH(s));
          const bhCamp      = todaysStudents.filter(s => isBH(s));
          const other       = todaysStudents.filter(s => !s.programSelection?.toLowerCase().includes('after school') && !s.programSelection?.toLowerCase().includes('camp') && !s.programSelection?.toLowerCase().includes('full-day') && !isBH(s));

          const breakdown = [
            { label: 'Total',       value: todaysStudents.length, color: '#3b82f6', show: true },
            { label: 'w/ Pickup',   value: withPickup.length,     color: '#00A3E0', show: withPickup.length > 0 },
            { label: 'Drop-off',    value: dropOff.length,        color: '#10b981', show: dropOff.length > 0 },
            { label: 'Camp (BH)',   value: bhCamp.length,         color: '#8b5cf6', show: bhCamp.length > 0 },
            { label: 'Camp',        value: camp.length,           color: '#f59e0b', show: camp.length > 0 },
            { label: 'Other',       value: other.length,          color: '#6b7280', show: other.length > 0 },
          ].filter(b => b.show);

          return (
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              {breakdown.map(b => (
                <div key={b.label} style={{ backgroundColor: 'white', borderRadius: '10px', padding: '0.85rem 1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `3px solid ${b.color}`, minWidth: '90px' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: '800', color: b.color, lineHeight: 1 }}>{b.value}</div>
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.2rem', fontWeight: '600' }}>{b.label}</div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Medical Alert Strip */}
        {(() => {
          const med = todaysStudents.filter(s => s.medicalConditions && s.medicalConditions !== 'None');
          if (!med.length) return null;
          return (
            <div style={{ marginBottom: '1rem', backgroundColor: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: '10px', padding: '0.85rem 1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                ⚠️ Medical Alerts — {med.length} student{med.length > 1 ? 's' : ''} today
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {med.map(s => (
                  <div key={s.id} style={{ backgroundColor: 'white', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.8rem' }}>
                    <span style={{ fontWeight: '700', color: '#991b1b' }}>{s.firstName} {s.lastName}</span>
                    <span style={{ color: '#dc2626', marginLeft: '0.4rem' }}>— {s.medicalConditions}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Staff Coverage */}
        {todaySchedules.length > 0 && (
          <div style={{ marginBottom: '1rem', backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '0.85rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
              👤 Staff Scheduled — {todaySchedules.length} shift{todaySchedules.length > 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {todaySchedules.map(schedule => (
                <div key={schedule.id} style={{ backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.8rem' }}>
                  <span style={{ fontWeight: '700', color: '#1e40af' }}>{schedule.staffName}</span>
                  <span style={{ color: '#3b82f6', marginLeft: '0.4rem' }}>
                    {schedule.startTime && schedule.endTime ? `${schedule.startTime}–${schedule.endTime}` : ''}
                  </span>
                  {schedule.programType && (
                    <span style={{ color: '#6b7280', marginLeft: '0.4rem', fontSize: '0.72rem' }}>· {schedule.programType}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {todaySchedules.length === 0 && (
          <div style={{ marginBottom: '1rem', backgroundColor: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a', padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#92400e' }}>
            {schedulePublished ? '⚠️ No staff scheduled for this day' : '🔒 Staff schedule not yet published for this week'}
          </div>
        )}

        <div style={styles.card}>
          {/* Filter and Search */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'all',      label: 'All',      color: '#3b82f6' },
                  { key: 'pickup',   label: 'Pickup',   color: '#00A3E0' },
                  { key: 'dropoff',  label: 'Drop-off', color: '#10b981' },
                  { key: 'camp',     label: 'Camp',     color: '#8b5cf6' },
                ].map(({ key, label, color }) => (
                  <button key={key}
                    style={{ ...styles.button, backgroundColor: todayFilter === key ? color : '#e5e7eb', color: todayFilter === key ? 'white' : '#374151', padding: '0.5rem 1rem' }}
                    onClick={() => setTodayFilter(key)}>
                    {label}
                  </button>
                ))}
              </div>
              <button style={{ ...styles.button, backgroundColor: '#10b981', padding: '0.5rem 1rem' }} onClick={handleExport}>
                📊 Export to Excel
              </button>
            </div>

            <input type="text" placeholder="🔍 Search by student or parent name..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...styles.input, marginBottom: 0, fontSize: '1rem', padding: '0.75rem' }} />
          </div>

          {/* Students Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Student</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Grade</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Program</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Medical/Allergies</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Emergency Contact</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Registration</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Form Status</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {todaysStudents
                  .sort((a, b) => {
                    const aFirst = (a.studentFirstName || a.firstName || '').trim().toLowerCase();
                    const aLast  = (a.studentLastName  || a.lastName  || '').trim().toLowerCase();
                    const bFirst = (b.studentFirstName || b.firstName || '').trim().toLowerCase();
                    const bLast  = (b.studentLastName  || b.lastName  || '').trim().toLowerCase();
                    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
                    return aLast.localeCompare(bLast);
                  })
                  .map((student, index) => {
                    const formValid = isFormValid(student.submissionDate);
                    return (
                      <tr key={student.id} style={{
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                        ...(student.medicalConditions && student.medicalConditions !== 'None' ? { borderLeft: '4px solid #dc2626' } : {})
                      }}>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                            {student.firstName} {student.lastName}
                          </div>
                          {student.authorizedPickup && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              Authorized: {student.authorizedPickup}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>{student.grade || '-'}</td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          <div style={{ color: '#6b7280' }}>{student.programSelection || '-'}</div>
                          {student.needsPickup && (
                            <div style={{ fontSize: '0.75rem', color: '#00A3E0', marginTop: '0.25rem' }}>🚗 Pickup Service</div>
                          )}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          {student.medicalConditions && student.medicalConditions !== 'None' ? (
                            <div style={{ color: '#dc2626', fontWeight: '600', backgroundColor: '#fee2e2', padding: '0.5rem', borderRadius: '6px' }}>
                              ⚠️ {student.medicalConditions}
                            </div>
                          ) : (
                            <span style={{ color: '#6b7280' }}>None</span>
                          )}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          <div style={{ fontWeight: '500', color: '#374151' }}>{student.parentName || '-'}</div>
                          <div style={{ color: student.parentPhone ? '#3b82f6' : '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem', fontWeight: student.parentPhone ? 'normal' : '700' }}>
                            📞 {student.parentPhone || '⚠️ Missing — ask at drop-off'}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>✉️ {student.parentEmail || '-'}</div>
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          {formValid ? (
                            <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}>✓ Valid</span>
                          ) : (
                            <span style={{ ...styles.badge, backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>⚠ Expired</span>
                          )}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          {student.registrationComplete ? (
                            <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}>✅ Complete</span>
                          ) : (
                            <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24' }}>⚠️ Pending</span>
                          )}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          {(() => {
                            // New = first-ever day at the center (attendance, care dates, or start date)
                            const isNew = isNewStudentOnDate(student, displayDate, firstAttendanceMap);
                            return isNew
                              ? <span style={{ ...styles.badge, backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', fontWeight: '700' }}>🆕 New</span>
                              : <span style={{ ...styles.badge, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}>↩ Returning</span>;
                          })()}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          {student.programSelection?.includes('After School') && (
                            <button onClick={() => setEditingStudent(student)}
                              style={{ padding: '0.5rem 1rem', backgroundColor: '#00A3E0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>
                              📅 Edit Schedule
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>

            {todaysStudents.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <Calendar style={{ width: '48px', height: '48px', color: '#d1d5db', margin: '0 auto 1rem auto' }} />
                <p style={{ color: '#6b7280', fontSize: '1rem' }}>No students scheduled for this day</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingStudent && (
        <EditAfterSchoolStudentModal
          student={editingStudent}
          onClose={() => setEditingStudent(null)}
          onSave={handleSaveAfterSchoolStudent}
        />
      )}
    </div>
  );
};

export default TodaysRoster;